import React, { useState, useEffect, useMemo } from 'react';
import {
  RequestTracerHeaderRow,
  RequestTracerHeading,
  RequestTracerTableContainer,
  FullWidthRequestTracerWrapper,
  BorderedSearchIcon,
} from './styles';
import { ResizableTable } from '../Templates/ResizableTable';
import {
  requestTracerInvariantQualificationColumns,
  requestTracerSlotQualificationColumns,
} from '../Templates/columns';
import SlotQualificationDetailPanel from './SlotQualificationDetailPanel';
import InvariantQualificationDetailPanel from './InvariantQualificationDetailPanel';
import RequestTracerSearchModal from './RequestTracerSearchModal';
import { connect } from 'dva';
import { SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, message, Spin } from 'antd';

const HIGHLIGHT_CLASS = 'selected-row';

const addRowKeys = (data) =>
  Array.isArray(data)
    ? data.map((row, idx) => ({
        ...row,
        key: row.key !== undefined ? row.key : idx,
        requestBody: row['request-body'],
        responseBody: row['response-body'],
      }))
    : [];

const RequestTracerTabs = ({
  traceId: initialTraceId,
  adId: initialAdId,
  tab: initialTabKey = '1',
  rowKey: initialRowKey = null,
  invariantWidths = {},
  slotWidths = {},
  traceData,
  loading,
  dispatch,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse query params from URL
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      urlTraceId: params.get('traceId') || '',
      urlAdId: params.get('adId') || '',
      urlTab: params.get('tab') || '1',
      urlRowKey: params.get('rowKey'),
    };
  };

  const invariantData = traceData?.invariantQualification || [];
  const slotData = traceData?.slotQualification || [];
  const pauseData = traceData?.pauseQualification || [];

  // Determine if we should show pause qualification instead of invariant
  const hasPauseData = pauseData.length > 0;
  const displayData = hasPauseData ? pauseData : invariantData;
  const tabLabel = hasPauseData ? 'Pause Qualification' : 'Invariant Qualification';

  const invariantRows = useMemo(() => addRowKeys(displayData), [displayData]);
  const slotRows = useMemo(() => addRowKeys(slotData), [slotData]);

  // Function to determine row class for highlighting
  const getRowClassName = (record) => {
    return String(record.key) === String(selectedRowKey) ? HIGHLIGHT_CLASS : '';
  };

  const [selectedRowKey, setSelectedRowKey] = useState(initialRowKey);
  const [detailPanelOpen, setDetailPanelOpen] = useState(!!initialRowKey);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTabKey);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [traceId, setTraceId] = useState(initialTraceId || '');
  const [adId, setAdId] = useState(initialAdId || '');
  const [navigating, setNavigating] = useState(false);

  // Helper to generate shareable link with all params
  const getShareableLink = () => {
    const params = new URLSearchParams({
      traceId,
      adId,
      tab: activeTab,
      rowKey: selectedRowKey || '',
    });
    return `${window.location.pathname}?${params.toString()}`;
  };

  // Centralized share handler
  const handleShare = () => {
    const params = new URLSearchParams();
    params.set('traceId', traceId ?? '');
    params.set('adId', adId ?? '');
    params.set('tab', activeTab ?? '1');
    params.set('rowKey', selectedRowKey ?? '');
    const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(shareUrl);
    message.success('Shareable link copied!');
  };

  // On initial mount, sync state from URL params (for deep-linking)
  useEffect(() => {
    const { urlTraceId, urlAdId, urlTab, urlRowKey } = getQueryParams();
    if (urlTraceId && urlAdId) {
      setTraceId(urlTraceId);
      setAdId(urlAdId);
      setActiveTab(urlTab);
      setSelectedRowKey(urlRowKey || null);
      setDetailPanelOpen(!!urlRowKey);
    }
    // After initial mount, ignore URL changes
    // eslint-disable-next-line
  }, []);

  // When traceData or selectedRowKey changes, update selectedRowData
  useEffect(() => {
    // Always compare keys as strings
    const rows = activeTab === '1' ? invariantRows : slotRows;
    const selectedRow = rows.find((row) => String(row.key) === String(selectedRowKey));
    setSelectedRowData(selectedRow || null);
    setNavigating(false);
    // Only open the panel if the row is found and the key is not null/undefined (0 is valid)
    setDetailPanelOpen(selectedRowKey != null && selectedRow != null);
    if (selectedRowKey != null && !selectedRow && rows.length > 0) {
      message.error('No data found for the selected row.');
    }
  }, [traceData, selectedRowKey, activeTab, invariantRows, slotRows]);

  const handleRowClick = (record) => ({
    onClick: () => {
      setSelectedRowKey(record.key);
      setSelectedRowData(record);
      setDetailPanelOpen(true);
      setNavigating(false);
    },
    style: { cursor: 'pointer' },
  });

  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setSelectedRowKey(null);
      setSelectedRowData(null);
      setDetailPanelOpen(false);
    }
  };

  const openSearchModal = () => {
    setSearchModalOpen(true);
    setSearchError(null);
  };

  const handleSearch = (newTraceId, newAdId) => {
    if (!newTraceId || !newAdId) {
      setSearchError('Both Trace ID and Ad ID are required.');
      return;
    }
    setSearchError(null);
    setTraceId(newTraceId);
    setAdId(newAdId);
    dispatch({
      type: 'requestTracer/fetchTraces',
      payload: { traceId: newTraceId, adId: newAdId, env: '' },
    });
  };

  return (
    <FullWidthRequestTracerWrapper>
      <RequestTracerHeaderRow>
        <RequestTracerHeading>RequestTracer - {traceId}</RequestTracerHeading>
        <BorderedSearchIcon title="Search" onClick={openSearchModal}>
          <SearchOutlined />
        </BorderedSearchIcon>
      </RequestTracerHeaderRow>
      <Spin spinning={loading || navigating} tip="Loading...">
        <Tabs defaultActiveKey="1" activeKey={activeTab} onChange={handleTabChange}>
          <Tabs.TabPane tab={tabLabel} key="1">
            <RequestTracerTableContainer data-testid="REQUEST_TRACER_INVARIANT_TABLE">
              <ResizableTable
                dataSource={invariantRows}
                inputColumns={requestTracerInvariantQualificationColumns(invariantRows, invariantWidths)}
                scroll={{ x: '100vw', y: '45vh' }}
                className="full-width-table"
                rowClassName={getRowClassName}
                onRow={handleRowClick}
                rowKey="key"
                searchableProps={false}
              />
            </RequestTracerTableContainer>
          </Tabs.TabPane>
          {!hasPauseData && (
            <Tabs.TabPane tab="Slot Qualification" key="2">
              <RequestTracerTableContainer data-testid="REQUEST_TRACER_SLOT_TABLE">
                <ResizableTable
                  dataSource={slotRows}
                  inputColumns={requestTracerSlotQualificationColumns(slotRows, slotWidths)}
                  scroll={{ x: '100vw', y: '45vh' }}
                  className="full-width-table"
                  rowClassName={getRowClassName}
                  onRow={handleRowClick}
                  rowKey="key"
                  searchableProps={false}
                />
              </RequestTracerTableContainer>
            </Tabs.TabPane>
          )}
        </Tabs>
        {activeTab === '1' && detailPanelOpen && selectedRowData && (
          <InvariantQualificationDetailPanel
            isOpen={detailPanelOpen}
            onClose={() => setDetailPanelOpen(false)}
            data={selectedRowData}
            adId={adId}
            handleShare={handleShare}
            loading={loading}
          />
        )}
        {activeTab === '2' && detailPanelOpen && selectedRowData && (
          <SlotQualificationDetailPanel
            isOpen={detailPanelOpen}
            onClose={() => setDetailPanelOpen(false)}
            data={selectedRowData}
            adId={adId}
            handleShare={handleShare}
            loading={loading}
          />
        )}
      </Spin>
      <RequestTracerSearchModal
        visible={searchModalOpen}
        onCancel={() => setSearchModalOpen(false)}
        onSearch={handleSearch}
        loading={loading}
      />
    </FullWidthRequestTracerWrapper>
  );
};

export default connect(({ requestTracer, loading }) => ({
  traceData: requestTracer.traceData,
  loading: loading.effects['requestTracer/fetchTraces'],
  error: requestTracer.error,
}))(RequestTracerTabs);
